import { useEffect, useRef, useState, type ReactNode } from 'react';
import { FileText, FolderOpen, Paperclip, Plus, Upload, X } from 'lucide-react';
import type { EntityAttachment } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { RichTextEditor, SearchableListSelect } from '../common';
import { getAttachmentUploadUrl, listEntityAttachments } from '../../api/entity-attachments';
import { htmlToPlainText, isValidEmail, plainTextToHtml } from './composeEmailUtils';
import { WritingAssistant } from './WritingAssistant';
import { FilePickerModal } from '../files/FilePickerModal';

const MAX_ATTACHMENT_TOTAL_BYTES = 7 * 1024 * 1024;

interface PendingAttachment {
  s3Key: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number | null;
}

/** A pre-selected, locked recipient (e.g. a vendor with a resolved email). */
export interface ComposeEmailRecipientChip {
  id: string;
  label: string;
  email: string | null;
}

export interface ComposeEmailPayload {
  subject: string;
  bodyHtml: string;
  attachments: { s3Key: string; fileName: string; mimeType: string }[];
  /**
   * Files chosen from the library, by id.
   *
   * Separate from `attachments` deliberately: those name an object key and are
   * guarded by a prefix allowlist, these name a row and are resolved against
   * what the caller may actually see. One list would mean one weaker rule.
   */
  fileIds: string[];
  ccSelf: boolean;
  /** Manually-typed recipient emails (in addition to the fixed recipients). */
  additionalEmails: string[];
}

export interface ComposeEmailSendOutcome {
  sentCount: number;
  skipped?: { label: string; reason: string }[];
}

export interface ComposeEmailModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Pre-selected, non-removable recipients. */
  fixedRecipients?: ComposeEmailRecipientChip[];
  /** Allow the user to type extra recipient emails. */
  allowManualRecipients?: boolean;
  /** When set, offer attachments from this event's documents. */
  eventIdForDocuments?: string;
  /** Allow uploading an attachment from the device (default true). */
  allowDeviceUpload?: boolean;
  /** Show the "send a copy to myself" checkbox (default true). */
  showCcSelf?: boolean;
  /** Extra caller-controlled options rendered above the attachments. */
  extraOptions?: ReactNode;
  /** Button label given the recipient count. */
  sendLabel?: (recipientCount: number) => string;
  onSend: (payload: ComposeEmailPayload) => Promise<ComposeEmailSendOutcome>;
}

/**
 * Generic, reusable email composer. Handles recipient chips (fixed + manual),
 * subject, rich-text body, attachments (event documents + device upload),
 * a "copy to myself" option, and the AI writing assistant. The caller
 * supplies `onSend`, so the same modal drives vendor emails, client emails, or
 * any other ad-hoc send.
 */
export function ComposeEmailModal({
  open,
  onClose,
  title = 'Compose Email',
  fixedRecipients = [],
  allowManualRecipients = false,
  eventIdForDocuments,
  allowDeviceUpload = true,
  showCcSelf = true,
  extraOptions,
  sendLabel,
  onSend,
}: ComposeEmailModalProps) {
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  // Library files, held by id and name: the id is what the server resolves, the
  // name is what the chip shows. No object key ever reaches this component.
  const [libraryFiles, setLibraryFiles] = useState<{ id: string; filename: string }[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [eventDocuments, setEventDocuments] = useState<EntityAttachment[]>([]);
  const [manualEmails, setManualEmails] = useState<string[]>([]);
  const [manualInput, setManualInput] = useState('');
  const [ccSelf, setCcSelf] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<ComposeEmailSendOutcome | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setSubject('');
    setBodyHtml('');
    setAttachments([]);
    setLibraryFiles([]);
    setPickerOpen(false);
    setManualEmails([]);
    setManualInput('');
    setCcSelf(false);
    setError(null);
    setOutcome(null);
    if (eventIdForDocuments) {
      listEntityAttachments('EVENT', eventIdForDocuments)
        .then((docs) => setEventDocuments(docs.filter((doc) => doc.s3Key && doc.fileName)))
        .catch(() => setEventDocuments([]));
    } else {
      setEventDocuments([]);
    }
  }, [open, eventIdForDocuments]);

  const totalBytes = attachments.reduce((sum, a) => sum + (a.sizeBytes ?? 0), 0);
  const fixedEmailCount = fixedRecipients.filter((r) => r.email).length;
  const recipientCount = fixedEmailCount + manualEmails.length;

  function addAttachment(next: PendingAttachment) {
    setAttachments((prev) => (prev.some((a) => a.s3Key === next.s3Key) ? prev : [...prev, next]));
  }

  function handlePickEventDocument(attachmentId: string) {
    const doc = eventDocuments.find((d) => d.id === attachmentId);
    if (!doc?.s3Key || !doc.fileName) return;
    addAttachment({
      s3Key: doc.s3Key,
      fileName: doc.fileName,
      mimeType: doc.mimeType ?? 'application/octet-stream',
      sizeBytes: doc.fileSizeBytes ?? null,
    });
  }

  async function handleDeviceUpload(file: File) {
    setError(null);
    if (totalBytes + file.size > MAX_ATTACHMENT_TOTAL_BYTES) {
      setError('Attachments exceed the 7MB email limit.');
      return;
    }
    setUploading(true);
    try {
      const mimeType = file.type || 'application/octet-stream';
      const { uploadUrl, s3Key } = await getAttachmentUploadUrl(file.name, mimeType);
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': mimeType },
        body: file,
      });
      if (!response.ok) throw new Error('Upload failed');
      addAttachment({ s3Key, fileName: file.name, mimeType, sizeBytes: file.size });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  function addManualEmail() {
    const email = manualInput.trim();
    if (!email) return;
    if (!isValidEmail(email)) {
      setError(`"${email}" is not a valid email address.`);
      return;
    }
    setError(null);
    setManualEmails((prev) =>
      prev.some((e) => e.toLowerCase() === email.toLowerCase()) ? prev : [...prev, email],
    );
    setManualInput('');
  }

  async function handleSend() {
    setError(null);
    setSending(true);
    try {
      const result = await onSend({
        subject: subject.trim(),
        bodyHtml,
        fileIds: libraryFiles.map((file) => file.id),
        attachments: attachments.map(({ s3Key, fileName, mimeType }) => ({
          s3Key,
          fileName,
          mimeType,
        })),
        ccSelf,
        additionalEmails: manualEmails,
      });
      setOutcome(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send the email');
    } finally {
      setSending(false);
    }
  }

  const bodyIsEmpty = !htmlToPlainText(bodyHtml);

  // The assistant works in plain text: the model is asked for prose, and a
  // suggestion that came back as markup would replace a formatted draft with
  // tag soup on accept.
  const assistant = (
    <WritingAssistant
      text={htmlToPlainText(bodyHtml)}
      // The markup, so a formatting-only edit still counts as the draft moving
      // on: `text` alone is identical across one, and a suggestion requested
      // before it would install and then flatten the new formatting on accept.
      revision={bodyHtml}
      onAccept={(text) => setBodyHtml(plainTextToHtml(text))}
      disabled={sending}
      onError={setError}
    />
  );
  const canSend = recipientCount > 0 && !!subject.trim() && !bodyIsEmpty && !sending && !uploading;

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        outcome ? (
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            data-testid="compose-close-after-send"
          >
            Done
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={!canSend}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              data-testid="compose-send"
            >
              {sending
                ? 'Sending…'
                : sendLabel
                  ? sendLabel(recipientCount)
                  : `Send to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`}
            </button>
          </div>
        )
      }
    >
      {outcome ? (
        <div className="space-y-3" data-testid="compose-result">
          <p className="text-sm text-green-700">
            Sent to {outcome.sentCount} recipient{outcome.sentCount === 1 ? '' : 's'}.
          </p>
          {outcome.skipped && outcome.skipped.length > 0 && (
            <div className="text-sm text-amber-700">
              <p className="font-medium">Skipped:</p>
              <ul className="list-disc ml-5">
                {outcome.skipped.map((skip, i) => (
                  <li key={`${skip.label}-${i}`}>
                    {skip.label} — {skip.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {error && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <p className="block text-sm font-medium text-gray-700 mb-1">To</p>
            {fixedRecipients.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="fixed-recipient-chips">
                {fixedRecipients.map((recipient) => (
                  <span
                    key={recipient.id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 text-xs rounded-full"
                  >
                    {recipient.label}
                    {recipient.email && (
                      <span className="text-primary-400">({recipient.email})</span>
                    )}
                  </span>
                ))}
              </div>
            )}
            {manualEmails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="manual-recipient-chips">
                {manualEmails.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full"
                  >
                    {email}
                    <button
                      type="button"
                      aria-label={`Remove ${email}`}
                      onClick={() => setManualEmails((prev) => prev.filter((e) => e !== email))}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {allowManualRecipients && (
              <div className="flex gap-2 mt-2">
                <input
                  type="email"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addManualEmail();
                    }
                  }}
                  placeholder="Add another email address…"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  data-testid="manual-email-input"
                />
                <button
                  type="button"
                  onClick={addManualEmail}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50"
                  data-testid="add-manual-email"
                >
                  <Plus size={14} />
                  Add
                </button>
              </div>
            )}
          </div>

          <div>
            <label
              htmlFor="compose-email-subject"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Subject
            </label>
            <input
              id="compose-email-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              data-testid="compose-email-subject"
            />
          </div>

          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="block text-sm font-medium text-gray-700">Message</p>
            </div>
            <RichTextEditor
              value={bodyHtml}
              onChange={setBodyHtml}
              placeholder="Write your message…"
              ariaLabel="Email body"
              disabled={sending}
            />
            {assistant}
          </div>

          {extraOptions}

          {showCcSelf && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={ccSelf}
                onChange={(e) => setCcSelf(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                data-testid="cc-self"
              />
              Send a copy to myself
            </label>
          )}

          {/* Always shown now: the library is a source every caller has. */}
          {true && (
            <div className="space-y-2">
              <p className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <Paperclip size={14} />
                Attachments
              </p>
              {attachments.length > 0 && (
                <ul className="space-y-1" data-testid="attachment-list">
                  {attachments.map((attachment) => (
                    <li
                      key={attachment.s3Key}
                      className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700"
                    >
                      <FileText size={14} className="text-gray-400 shrink-0" />
                      <span className="flex-1 truncate">{attachment.fileName}</span>
                      {attachment.sizeBytes != null && (
                        <span className="text-xs text-gray-400">
                          {(attachment.sizeBytes / 1024 / 1024).toFixed(2)} MB
                        </span>
                      )}
                      <button
                        type="button"
                        aria-label={`Remove ${attachment.fileName}`}
                        onClick={() =>
                          setAttachments((prev) => prev.filter((a) => a.s3Key !== attachment.s3Key))
                        }
                        className="p-0.5 text-gray-400 hover:text-red-600"
                      >
                        <X size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {libraryFiles.length > 0 && (
                <ul className="space-y-1" data-testid="library-attachment-list">
                  {libraryFiles.map((file) => (
                    <li
                      key={file.id}
                      className="flex items-center gap-2 px-2.5 py-1.5 bg-primary-50 border border-primary-200 rounded-lg text-sm text-gray-700"
                    >
                      <FolderOpen size={14} className="text-primary-500 shrink-0" />
                      <span className="flex-1 truncate">{file.filename}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${file.filename}`}
                        onClick={() =>
                          setLibraryFiles((prev) => prev.filter((f) => f.id !== file.id))
                        }
                        className="p-0.5 text-gray-400 hover:text-red-600"
                      >
                        <X size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {eventIdForDocuments && (
                  <div data-testid="event-document-picker">
                    <SearchableListSelect
                      items={eventDocuments.filter(
                        (doc) => !attachments.some((a) => a.s3Key === doc.s3Key),
                      )}
                      value={null}
                      onChange={(picked) => {
                        if (typeof picked === 'string' && picked) handlePickEventDocument(picked);
                      }}
                      getValue={(doc) => doc.id}
                      getLabel={(doc) => doc.fileName ?? 'Document'}
                      placeholder="Add from event documents…"
                      ariaLabel="Add from event documents"
                      clearable={false}
                      emptyMessage="No documents on this event yet."
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-gray-700 border border-gray-200 border-dashed rounded-lg hover:bg-gray-50"
                  data-testid="attach-from-files"
                >
                  <FolderOpen size={14} />
                  Attach from files
                </button>
                {allowDeviceUpload && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-gray-700 border border-gray-200 border-dashed rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    data-testid="upload-from-device"
                  >
                    <Upload size={14} />
                    {uploading ? 'Uploading…' : 'Upload from device'}
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  data-testid="device-file-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleDeviceUpload(file);
                    e.target.value = '';
                  }}
                />
              </div>
              {totalBytes > 0 && (
                <p className="text-xs text-gray-500">
                  Total {(totalBytes / 1024 / 1024).toFixed(2)} MB of 7 MB
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/*
        Mounted only while open, not merely hidden. `useFileExplorer` fetches
        the tree as soon as it runs, so an always-mounted picker would load the
        whole file library every time somebody opened the composer to type two
        lines — and would do it before knowing whether they wanted a file at all.
      */}
      {pickerOpen && (
        <FilePickerModal
          open
          onClose={() => setPickerOpen(false)}
          onPick={(picked) =>
            // De-duped by id: picking the same file twice would attach it twice.
            setLibraryFiles((prev) => {
              const seen = new Set(prev.map((file) => file.id));
              return [...prev, ...picked.filter((file) => !seen.has(file.id))];
            })
          }
          {...(eventIdForDocuments
            ? // Lower case: a node id is parsed as `<entityType>:<entityId>` and
              // the package refuses a type outside /^[a-z][a-z0-9_-]*$/.
              // `EVENT:` here is what made this dialog answer 500 on open.
              { rootNodeId: `event:${eventIdForDocuments}` }
            : {})}
        />
      )}
    </EntityModal>
  );
}
